import { useState } from 'react';
import {
  LayoutDashboard,
  FileSpreadsheet,
  DollarSign,
  Database,
  Key,
  Settings,
  AlertTriangle,
  LogOut,
  Menu,
  X,
  User,
  ChevronDown,
  Wallet,
  Receipt,
  Mail,
  Users,
  UserPlus,
  BarChart2,
  Upload,
  Share2,
  UserCheck,
  Bell,
} from 'lucide-react';

interface AdminUser {
  id: string;
  email: string;
  fullName: string | null;
  role: 'super_admin' | 'admin' | 'viewer';
}

type AdminPage = 'dashboard' | 'sms-analytics' | 'batches' | 'reports' | 'cache' | 'cache-management' | 'api-keys' | 'settings' | 'issues' | 'billing' | 'billing-rates' | 'email-templates' | 'notifications' | 'users' | 'team-members' | 'affiliates' | 'prospects';

interface AdminLayoutProps {
  user: AdminUser | null;
  currentPage: AdminPage;
  onNavigate: (page: AdminPage) => void;
  onLogout: () => void;
  children: React.ReactNode;
}

const navItems: { id: AdminPage; label: string; icon: typeof LayoutDashboard }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'sms-analytics', label: 'SMS Analytics', icon: BarChart2 },
  { id: 'batches', label: 'All Batches', icon: FileSpreadsheet },
  { id: 'billing', label: 'Location Billing', icon: Wallet },
  { id: 'billing-rates', label: 'Billing Rates', icon: Receipt },
  { id: 'team-members', label: 'Team Members', icon: UserPlus },
  { id: 'affiliates', label: 'Affiliates', icon: Share2 },
  { id: 'prospects', label: 'Prospects', icon: UserCheck },
  { id: 'email-templates', label: 'Email Templates', icon: Mail },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'reports', label: 'Revenue Reports', icon: DollarSign },
  { id: 'cache', label: 'Cache Stats', icon: Database },
  { id: 'cache-management', label: 'Cache Import', icon: Upload },
  { id: 'api-keys', label: 'API Keys', icon: Key },
  { id: 'users', label: 'Admin Users', icon: Users },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'issues', label: 'Issue Log', icon: AlertTriangle },
];

export default function AdminLayout({
  user,
  currentPage,
  onNavigate,
  onLogout,
  children,
}: AdminLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-900 transform transition-transform duration-200 ease-in-out lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 bg-gray-800 flex-shrink-0">
          <span className="text-xl font-bold text-white">OAK Admin</span>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1 text-gray-400 hover:text-white lg:hidden"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentPage === item.id;

            return (
              <button
                key={item.id}
                onClick={() => {
                  onNavigate(item.id);
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-4 py-3 mb-1 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 mr-3" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* User info at bottom */}
        <div className="flex-shrink-0 p-4 border-t border-gray-800">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-gray-300" />
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {user?.fullName || user?.email}
              </p>
              <span
                className={`inline-block px-2 py-0.5 text-xs rounded ${getRoleBadgeColor(
                  user?.role || ''
                )}`}
              >
                {user?.role?.replace('_', ' ')}
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-gray-600 hover:text-gray-900 lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Page title - visible on desktop */}
            <h1 className="hidden lg:block text-xl font-semibold text-gray-900">
              {navItems.find((item) => item.id === currentPage)?.label || 'Dashboard'}
            </h1>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <span className="hidden sm:block text-sm font-medium text-gray-700">
                  {user?.email}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>

              {/* Dropdown */}
              {userMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setUserMenuOpen(false)}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user?.fullName || 'Admin User'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setUserMenuOpen(false);
                        onLogout();
                      }}
                      className="w-full flex items-center px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
